/**
 * Welcome Screen Configuration
 * Easy to customize - change colors, text, icons, or layout!
 *
 * WIREFRAME DESIGN - Feel free to modify to match your brand
 * All visual elements are configurable here for easy future changes
 */

/**
 * Color Scheme - Tailwind classes
 * Change these to match your desired theme
 */
export const welcomeColors = {
  // Background - light pink (#FFE4E8)
  background: 'bg-pink-100',

  // Container box - white
  containerBackground: 'bg-white',
  containerShadow: 'shadow-lg',
  containerRounded: 'rounded-lg',

  // Buttons - all pink
  buttonBackground: 'bg-pink-200',
  buttonHover: 'hover:bg-pink-300',
  buttonText: 'text-gray-800',
  buttonRounded: 'rounded-lg',

  // Header text
  titleText: 'text-black',
  subtitleText: 'text-black',

  // Footer text
  footerText: 'text-gray-400',

  // Icon color
  iconColor: 'text-black'
}

/**
 * UI Text
 * All text used in the welcome screen
 */
export const welcomeText = {
  // Header
  title: '',
  subtitle: '', // Removed 'Welcome' text

  // Button labels
  textButton: 'text',
  galleryButton: 'gallery',
  worldsButton: 'worlds',
  accountButton: 'account',
  vaultButton: 'vault',
  statusButton: 'status',
  newsroomButton: 'newsroom',

  // Footer
  footerPrefix: 'Logged in as'
}

/**
 * Layout Configuration
 * Defines button arrangement and spacing
 */
export const welcomeLayout = {
  // Container sizing
  maxWidth: 'max-w-md',
  padding: 'px-4',

  // Spacing
  headerMarginBottom: 'mb-8',
  containerPadding: 'p-6',
  buttonGap: 'gap-4',

  // Button heights
  buttonMinHeight: 'min-h-[60px]', // Thinner buttons

  // Icon size
  iconSize: 32,
  iconMarginBottom: 'mb-2',

  // Animations
  enableAnimations: true,
  animationDuration: 'animate-slide-up',
  animationDelay: 'animate-delay-100',
  fadeInAnimation: 'animate-fade-in'
}

/**
 * Button Configuration
 * Defines all buttons, their icons, routes, and positions
 *
 * Layout:
 * Row 1: Text (full width)
 * Row 2: Gallery (full width)
 * Row 3: Account, Vault, Groupchat, Poke (4 columns)
 */
export const welcomeButtons = {
  // Row 1 - Full width
  row1: [
    {
      id: 'text',
      label: welcomeText.textButton,
      route: '/messages',
      icon: 'text' as const
    }
  ],

  // Row 2 - Full width
  row2: [
    {
      id: 'gallery',
      label: welcomeText.galleryButton,
      route: '/gallery',
      icon: 'gallery' as const
    }
  ],

  // Row 3 - 4 columns (2x2 grid on mobile, 4 columns on desktop)
  row3: [
    {
      id: 'account',
      label: welcomeText.accountButton,
      route: '/account',
      icon: 'account' as const
    },
    {
      id: 'status',
      label: welcomeText.statusButton,
      route: '/status',
      icon: 'status' as const
    },
    {
      id: 'vault',
      label: welcomeText.vaultButton,
      route: '/vault',
      icon: 'vault' as const
    },
    {
      // Swapped with News: Worlds now sits in the small icon-only row-3 slot that
      // previously held Newsroom. News was promoted to the prominent full-width
      // button (rendered in WelcomeScreen.tsx). Both routes remain active.
      id: 'worlds',
      label: welcomeText.worldsButton,
      route: '/worlds',
      icon: 'worlds' as const
    }
  ]
}

/**
 * Feature Flags
 * Enable/disable features easily
 */
export const welcomeFeatures = {
  // Show user email at bottom
  showUserInfo: true,

  // Show animations
  enableAnimations: true,

  // Enable button hover effects
  enableHoverEffects: true,

  // Show mailbox icon for address collection
  showMailbox: true
}
